import React, { useState, useRef, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { formatNumber } from "../../../utils/DefaultFunction";

const FifteenDaySummary = ({ data }) => {
    const chartRef = useRef(null);
    const channelColors = {
        sms: "#93c5fd",
        whatsapp: "#6ee7b7",
        email: "#fcd34d",
        voice: "#ddd6fe",
        push: "#fca5a5",
        inapp: "#c4b5fd"
    };

    // Dynamically detect which channels exist in the data
    const availableChannels = useMemo(() => {
        const channels = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'date' && channelColors[key]) {
                    channels.add(key);
                }
            });
        });
        return Array.from(channels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Initialize visible channels state based on available channels
    const [visibleChannels, setVisibleChannels] = useState(() => {
        const initial = {};
        availableChannels.forEach(channel => {
            initial[channel] = true;
        });
        return initial;
    });
    if (!data || data.length === 0) return <div>No data</div>;

    // Define colors for all possible channels



    // Capitalize first letter
    const capitalize = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Extract dates for x-axis
    const dates = data.map(item => item.date);

    // Toggle channel visibility - click to isolate or show all
    const handleLegendClick = (channel) => {
        setVisibleChannels(prev => {
            const isCurrentlyVisible = prev[channel];
            const onlyOneVisible = Object.values(prev).filter(v => v).length === 1;

            // If clicking the only visible channel, show all
            if (onlyOneVisible && isCurrentlyVisible) {
                const allVisible = {};
                availableChannels.forEach(ch => {
                    allVisible[ch] = true;
                });
                return allVisible;
            }

            // Otherwise, show only the clicked channel
            const isolated = {};
            availableChannels.forEach(ch => {
                isolated[ch] = ch === channel;
            });
            return isolated;
        });
    };

    // Create series data only for available channels
    const seriesData = availableChannels.map((channel) => ({
        name: capitalize(channel),
        data: data.map(item => item[channel] || 0),
        color: channelColors[channel],
        visible: visibleChannels[channel],
        fillOpacity: 0.5,
        marker: {
            enabled: true,
            symbol: 'circle',
            radius: 4,
            lineWidth: 2,
            lineColor: '#ffffff',
            fillColor: channelColors[channel]
        },
        states: {
            hover: {
                marker: {
                    radius: 6,
                    lineWidth: 2,
                    lineColor: '#ffffff'
                }
            }
        },
        lineWidth: 2
    }));

    const options = {
        chart: {
            type: "area",
            height: 600,
            backgroundColor: "transparent",
            marginLeft: 60,
            marginRight: 30,
            marginBottom: 100
        },
        title: {
            text: null
        },
        xAxis: {
            categories: dates,
            labels: {
                rotation: -20,
                style: {
                    fontSize: "11px",
                    color: "#666"
                }
            },
            tickmarkPlacement: 'between',
            startOnTick: true,
            endOnTick: true,
            gridLineWidth: 1,
            gridLineColor: "#E5E7EB",
            gridLineDashStyle: "Dash"
        },
        yAxis: {
            title: {
                text: "Count",
                style: {
                    fontSize: "12px",
                    color: "#666"
                }
            },
            labels: {
                style: {
                    fontSize: "12px",
                    color: "#666"
                }
            },
            gridLineColor: "#E5E7EB",
            gridLineDashStyle: "Dash"
        },

        tooltip: {
            shared: true,
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            borderColor: "#ccc",
            borderRadius: 4,
            borderWidth: 1,
            shadow: {
                offsetX: 0,
                offsetY: 2,
                width: 8,
                opacity: 0.15
            },
            useHTML: true,
            padding: 12,
            formatter: function () {
                // Filter only visible channels
                const visiblePoints = this.points.filter(point =>
                    visibleChannels[point.series.name.toLowerCase()]
                );

                if (visiblePoints.length === 0) return false;

                let tooltip = `<div style="font-weight: bold; font-size: 13px; margin-bottom: 8px;">${this.x}</div>`;

                visiblePoints.forEach(point => {
                    tooltip += `
                        <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0; font-size: 12px;">
                            <span style="width: 10px; height: 10px; border-radius: 50%; background-color: ${point.color}; display: inline-block;"></span>
                            <span style="color: #666;">
                                ${point.series.name}: <strong>${formatNumber(point.y)}</strong>
                            </span>
                        </div>
                    `;
                });

                return tooltip;
            }
        },
        plotOptions: {
            area: {
                stacking: undefined,
                lineWidth: 2,
                marker: {
                    enabled: true,
                    symbol: 'circle',
                    radius: 4
                },
                fillOpacity: 0.5,
                states: {
                    hover: {
                        lineWidth: 2
                    }
                }
            },
            series: {
                events: {
                    legendItemClick: function () {
                        const channelName = this.name.toLowerCase();
                        handleLegendClick(channelName);
                        return false;
                    }
                }
            }
        },
        legend: {
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            itemStyle: {
                color: "#666",
                fontSize: "14px",
                fontWeight: "normal",
                cursor: "pointer"
            },
            itemHoverStyle: {
                color: "#000"
            },
            symbolRadius: 5,
            symbolHeight: 10,
            symbolWidth: 10,
            itemMarginTop: 10,
            itemMarginBottom: 10,
            margin: 20,
            useHTML: true,
            labelFormatter: function () {
                const channel = this.name.toLowerCase();
                const isVisible = visibleChannels[channel];
                const opacity = isVisible ? 1 : 0.4;

                return `<span style="opacity: ${opacity}; transition: opacity 0.2s;">${this.name}</span>`;
            }
        },
        series: seriesData,
        credits: {
            enabled: false
        }
    };

    return (
        <div style={{ width: "100%", height: "600px" }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                ref={chartRef}
            />
        </div>
    );
};

export default FifteenDaySummary;